import React, { useState, useEffect } from "react";
import { apiFetch } from "../../../../context/AuthContext";
import { FIELD_SECTIONS } from "./EditEmployeeModal";
import {
  User,
  Building,
  DollarSign,
  Clock,
  GraduationCap,
  Shield,
  X,
  FileText,
  CheckCircle,
} from "../../constants/icons";

const API_URL = process.env.REACT_APP_BASE_URL;

// Icon per section key (FIELD_SECTIONS is shared with the edit modal).
const SECTION_ICONS = {
  employeeInfo: User,
  employment: Building,
  compensation: DollarSign,
  contract: FileText,
  insurance: Shield,
  education: GraduationCap,
  licenses: CheckCircle,
  documents: FileText,
  system: Clock,
};

// Fields shown as currency values.
const MONEY_FIELDS = new Set([
  "netSalary",
  "variableSalary",
  "transportationAllowances",
  "insuranceSalary",
]);

// Extra read-only section (not part of the editable catalog).
const SYSTEM_SECTION = {
  key: "system",
  title: { en: "System Information", ar: "معلومات النظام" },
  fields: [
    { name: "infinityCode", label: { en: "Infinity Code", ar: "كود إنفينيتي" }, type: "text" },
    { name: "id", label: { en: "Database ID", ar: "معرف قاعدة البيانات" }, type: "text" },
    { name: "created_at", label: { en: "Created At", ar: "تاريخ الإنشاء" }, type: "date" },
    { name: "updated_at", label: { en: "Last Updated", ar: "آخر تحديث" }, type: "date" },
  ],
};

// The edit modal keeps "Basic Information"/"Address" and "Work
// Information"/"Management" as four separate catalog sections; the view
// modal merges each pair into a single tab ("Employee Information" and
// "Employment Information") so related fields live together.
const findSection = (key) => FIELD_SECTIONS.find((section) => section.key === key);
const BASIC_SECTION = findSection("basic");
const ADDRESS_SECTION = findSection("address");
const WORK_SECTION = findSection("work");
const MANAGEMENT_SECTION = findSection("management");
const OTHER_SECTIONS = FIELD_SECTIONS.filter(
  (section) => !["basic", "address", "work", "management"].includes(section.key)
);

const EMPLOYEE_INFO_SECTION = {
  key: "employeeInfo",
  title: { en: "Employee Information", ar: "معلومات الموظف" },
  fields: [...(BASIC_SECTION?.fields || []), ...(ADDRESS_SECTION?.fields || [])],
};

const EMPLOYMENT_INFO_SECTION = {
  key: "employment",
  title: { en: "Employment Information", ar: "معلومات التوظيف" },
  fields: [...(WORK_SECTION?.fields || []), ...(MANAGEMENT_SECTION?.fields || [])],
};

const VIEW_SECTIONS = [
  EMPLOYEE_INFO_SECTION,
  EMPLOYMENT_INFO_SECTION,
  ...OTHER_SECTIONS,
  SYSTEM_SECTION,
];

// ===== RESPONSIVE VIEW EMPLOYEE MODAL COMPONENT =====
const ViewEmployeeModal = ({
  selectedEmployee = null,
  setShowViewEmployee = () => {},
  text = {},
  language = "en",
  screenSize = "md",
  styles = {},
  spacing = {},
  employeeProfilePics = {},
  getUserInitials = () => "",
  getStatusBadge = () => null,
  theme = "light",
  fonts = {},
  // Compensation tab (salaries + bank). Same rule as the server, which only
  // returns those fields to real admins and to admin levels with the
  // employees 'export' feature — without it the tab would only show blanks.
  canViewCompensation = true,
}) => {
  const visibleSections = canViewCompensation
    ? VIEW_SECTIONS
    : VIEW_SECTIONS.filter((section) => section.key !== "compensation");

  const infinityCode =
    selectedEmployee?.fullEmployeeData?.infinityCode ||
    selectedEmployee?.infinityCode ||
    selectedEmployee?.id ||
    null;

  // Seed with the row's locally loaded record; the complete record (all
  // database fields) is fetched right after mount and replaces it.
  const [record, setRecord] = useState(
    () => selectedEmployee?.fullEmployeeData || {}
  );
  const [isLoadingFullRecord, setIsLoadingFullRecord] = useState(false);
  const [fullRecordError, setFullRecordError] = useState(null);
  const [activeTab, setActiveTab] = useState(VIEW_SECTIONS[0].key);
  const [showFullImage, setShowFullImage] = useState(false);

  useEffect(() => {
    if (!infinityCode) return;
    let cancelled = false;

    const loadFullRecord = async () => {
      try {
        setIsLoadingFullRecord(true);
        setFullRecordError(null);
        const response = await apiFetch(
          `${API_URL}/employees/select/${infinityCode}`,
          { method: "GET" }
        );
        const json = await response.json().catch(() => null);
        const fullRecord = json?.employee || null;
        if (!response.ok || !fullRecord) {
          throw new Error(json?.message || "Failed to load employee record");
        }
        if (!cancelled) setRecord(fullRecord);
      } catch (err) {
        console.error("Failed to fetch full employee record:", err);
        if (!cancelled) setFullRecordError(err.message);
      } finally {
        if (!cancelled) setIsLoadingFullRecord(false);
      }
    };

    loadFullRecord();
    return () => {
      cancelled = true;
    };
  }, [infinityCode]);

  // Phones get a bottom sheet: sticky header with the close button, every tab
  // visible as a 2-column grid, and the fields as one plain label/value list.
  const phone = screenSize === "xs" || screenSize === "sm";
  const divider = theme === "dark" ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.08)";
  const sheetBg = theme === "dark" ? "#0f172a" : "#ffffff";

  // Provide default styles that match AddEmployeeModal look-and-feel
  const defaultStyles = {
    modal: {
      position: "fixed",
      inset: 0,
      display: "flex",
      alignItems: phone ? "flex-end" : "center",
      justifyContent: "center",
      background: "rgba(0,0,0,0.5)",
      zIndex: 9999,
      backdropFilter: "blur(3px)",
      animation: "fadeIn 300ms ease-out",
    },
    viewModalContent: {
      position: "relative",
      width: phone ? "100%" : "92%",
      maxWidth: 1000,
      maxHeight: phone ? "94dvh" : "92vh",
      overflowY: "auto",
      boxSizing: "border-box",
      background: phone
        ? sheetBg
        : theme === "dark"
          ? "linear-gradient(135deg, #0f172a 0%, #1a2a3a 100%)"
          : "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
      color: theme === "dark" ? "#e6eef8" : "#0f172a",
      borderRadius: phone ? "18px 18px 0 0" : 16,
      padding: phone ? "0 16px 24px" : "32px",
      boxShadow: theme === "dark"
        ? "0 25px 60px rgba(0,0,0,0.8), 0 0 1px rgba(255,255,255,0.1)"
        : "0 20px 50px rgba(0,0,0,0.12), 0 0 1px rgba(0,0,0,0.04)",
      border: theme === "dark"
        ? "1px solid rgba(255,255,255,0.1)"
        : "1px solid rgba(148,163,184,0.15)",
      scrollbarWidth: "none",
      msOverflowStyle: "none",
      WebkitOverflowScrolling: "touch",
      animation: "slideUp 400ms cubic-bezier(0.34, 1.56, 0.64, 1)",
    },
    modalHeader: {
      marginBottom: phone ? 4 : 24,
      paddingBottom: phone ? 12 : 16,
      borderBottom: theme === "dark"
        ? "1px solid rgba(255,255,255,0.08)"
        : "1px solid rgba(0,0,0,0.08)",
      display: "flex",
      alignItems: "center",
      gap: 12,
      justifyContent: "space-between",
      ...(phone && {
        position: "sticky",
        top: 0,
        zIndex: 5,
        background: sheetBg,
        margin: "0 -16px 4px",
        padding: "14px 16px 12px",
      }),
    },
    modalTitle: {
      margin: 0,
      fontSize: phone ? 18 : 24,
      fontWeight: 800,
      background: theme === "dark"
        ? "linear-gradient(135deg, #fff 0%, #cbd5e1 100%)"
        : "linear-gradient(135deg, #0f172a 0%, #0f172a 100%)",
      WebkitBackgroundClip: "text",
      WebkitTextFillColor: "transparent",
      backgroundClip: "text",
      letterSpacing: "-0.5px",
      display: "flex",
      alignItems: "center",
      gap: 10,
    },
    // In the header row (not floated over the content), so it never covers
    // the status badge and stays visible while the sheet scrolls.
    closeBtn: {
      flexShrink: 0,
      width: 40,
      height: 40,
      borderRadius: 10,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      background: theme === "dark" ? "rgba(13, 18, 28, 0.15)" : "rgba(255,255,255,0.92)",
      border: theme === "dark" ? "1px solid rgba(255,255,255,0.06)" : "1px solid rgba(15,23,42,0.06)",
      cursor: "pointer",
      transition: "all 200ms ease",
      zIndex: 20,
      color: theme === "dark" ? "#94a3b8" : "#475569",
    },
    viewModalBody: { display: "flex", flexDirection: "column", gap: 20 },
    employeeHeader: {
      display: "flex",
      gap: 16,
      alignItems: "center",
      padding: phone ? "8px 0 16px" : "20px 0",
      borderBottom: theme === "dark" ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,0,0,0.08)",
      marginBottom: 8,
    },
    largeAvatar: {
      width: phone ? 60 : 96,
      height: phone ? 60 : 96,
      borderRadius: "50%",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      color: "#fff",
      fontWeight: 700,
      fontSize: phone ? 20 : 28,
      flexShrink: 0,
    },
    employeeHeaderInfo: { flex: 1, minWidth: 0 },
    employeeName: { fontSize: phone ? 18 : 22, overflowWrap: "anywhere", fontWeight: 800, margin: "0 0 4px 0", color: theme === "dark" ? "#fff" : "#0f172a" },
    employeeTitle: { margin: "0 0 2px 0", fontSize: 14, color: theme === "dark" ? "#94a3b8" : "#475569" },
    employeeDepartment: { margin: 0, fontSize: 13, color: theme === "dark" ? "#60a5fa" : "#1e40af", fontWeight: 600 },
    employeeStatus: { marginTop: 8 },
    infoSection: {
      marginTop: 4,
      padding: phone ? "2px 14px" : "16px",
      background: theme === "dark" ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)",
      borderRadius: 12,
      border: theme === "dark" ? "1px solid rgba(255,255,255,0.06)" : "1px solid rgba(0,0,0,0.05)",
    },
    sectionHeader: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      fontWeight: 800,
      fontSize: 13,
      color: theme === "dark" ? "#60a5fa" : "#1e40af",
      borderLeft: theme === "dark" ? "3px solid rgba(96,165,250,0.6)" : "3px solid #3b82f6",
      paddingLeft: 10,
      marginBottom: 14,
      textTransform: "uppercase",
      letterSpacing: "0.6px",
    },
    infoGrid: {
      display: "grid",
      gridTemplateColumns: phone ? "1fr" : "repeat(3,1fr)",
      gap: phone ? 0 : 16,
    },
    infoItem: phone
      ? {
        display: "flex",
        padding: "12px 2px",
        borderBottom: `1px solid ${divider}`,
      }
      : {
        display: "flex",
        gap: 10,
        alignItems: "flex-start",
        padding: "10px 12px",
        borderRadius: 8,
        background: theme === "dark" ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)",
        border: theme === "dark" ? "1px solid rgba(255,255,255,0.05)" : "1px solid rgba(0,0,0,0.04)",
      },
    infoLabel: phone
      ? { fontSize: 12.5, fontWeight: 600, color: theme === "dark" ? "#94a3b8" : "#64748b", marginBottom: 3 }
      : { fontSize: 11, fontWeight: 700, color: theme === "dark" ? "#94a3b8" : "#475569", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.5px" },
    infoValue: { fontSize: phone ? 15 : 14, fontWeight: 500, color: theme === "dark" ? "#e6eef8" : "#0f172a", overflowWrap: "anywhere" },
    tabBar: {
      display: phone ? "grid" : "flex",
      ...(phone && { gridTemplateColumns: "repeat(2, minmax(0, 1fr))" }),
      gap: phone ? 6 : 4,
      overflowX: phone ? "visible" : "auto",
      overflowY: phone ? "visible" : "hidden",
      padding: 6,
      marginBottom: phone ? 12 : 20,
      background: theme === "dark" ? "rgba(255,255,255,0.03)" : "rgba(15,23,42,0.035)",
      borderRadius: 14,
      border: theme === "dark" ? "1px solid rgba(255,255,255,0.06)" : "1px solid rgba(0,0,0,0.05)",
    },
    tabButton: {
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      flexShrink: 0,
      padding: "10px 18px",
      borderRadius: 10,
      border: "1px solid transparent",
      background: "transparent",
      color: theme === "dark" ? "#8291a8" : "#64748b",
      fontSize: 13,
      fontWeight: 600,
      cursor: "pointer",
      whiteSpace: "nowrap",
      transition: "all 180ms cubic-bezier(0.4, 0, 0.2, 1)",
      // Phones: every tab is a labelled tile in the 2-column grid.
      ...(phone && {
        minHeight: 44,
        minWidth: 0,
        padding: "10px",
        fontSize: 12.5,
        whiteSpace: "normal",
        textAlign: "start",
        lineHeight: 1.25,
        background: theme === "dark" ? "rgba(255,255,255,0.04)" : "#ffffff",
        border: `1px solid ${divider}`,
      }),
    },
    tabButtonHover: {
      background: theme === "dark" ? "rgba(255,255,255,0.06)" : "rgba(255,255,255,0.9)",
      color: theme === "dark" ? "#e2e8f0" : "#1e293b",
    },
    tabButtonActive: {
      background: theme === "dark"
        ? "linear-gradient(135deg, #2e7cba 0%, #1d5a8c 100%)"
        : "linear-gradient(135deg, #2e7cba 0%, #3b82f6 100%)",
      color: "#ffffff",
    },
  };

  // merge passed styles over defaults (empty {} by default so defaultStyles win)
  styles = { ...defaultStyles, ...(styles || {}) };

  // Function to format date strings
  const formatDate = (dateString) => {
    if (!dateString) return text[language].na;
    const parsed = new Date(dateString);
    if (isNaN(parsed.getTime())) return text[language].na;
    return parsed.toLocaleDateString("en-US", {
      year: "numeric",
      month: screenSize === "xs" ? "short" : "long",
      day: "numeric",
    });
  };

  // Handle clicking outside modal to close it
  const handleModalClick = (e) => {
    if (e.target === e.currentTarget) {
      setShowViewEmployee(false);
    }
  };

  // Function to format currency amounts
  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined || amount === "" || amount === 0)
      return text[language].na;
    const numeric = Number(amount);
    if (isNaN(numeric)) return text[language].na;
    return `$${numeric.toLocaleString()}`;
  };

  const renderBooleanValue = (value) => (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "2px 10px",
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 700,
        background: value ? "rgba(16,185,129,0.12)" : "rgba(148,163,184,0.12)",
        color: value ? "#059669" : theme === "dark" ? "#94a3b8" : "#64748b",
      }}
    >
      {value
        ? language === "ar" ? "نعم ✓" : "Yes ✓"
        : language === "ar" ? "لا ✗" : "No ✗"}
    </span>
  );

  // Resolve a display value for any field of the shared catalog.
  const renderFieldValue = (field) => {
    const value = record?.[field.name];
    if (field.name === "firstLineManagerClientCode") {
      const name = record?.firstLineManagerName || "";
      return name ? `${value} — ${name}` : String(value);
    }
    if (field.type === "date") return formatDate(value);
    if (field.type === "department")
      return String(
        record?.department?.name ??
          selectedEmployee?.department ??
          text[language].na
      );
    if (MONEY_FIELDS.has(field.name)) return formatCurrency(value);
    if (value === null || value === undefined || value === "")
      return text[language].na;
    return String(value);
  };

  // Guard after hooks
  if (!selectedEmployee) return null;

  // Return modal JSX
  return (
    <>
    <div style={styles.modal} onClick={handleModalClick}>
      <div style={styles.viewModalContent}>
        {/* Modal Header */}
        <div style={styles.modalHeader}>
          <h2 style={styles.modalTitle}>{text[language].employeeDetails}</h2>
          <button style={styles.closeBtn} onClick={() => setShowViewEmployee(false)} title={text[language].close || 'Close'}>
            <X size={screenSize === "xs" ? 18 : 20} />
          </button>
        </div>

        {/* Full-record loading / error banner */}
        {isLoadingFullRecord && (
          <div
            style={{
              marginBottom: 16,
              padding: "10px 14px",
              borderRadius: 10,
              background: theme === "dark"
                ? "rgba(46,124,186,0.12)"
                : "rgba(46,124,186,0.08)",
              color: "#2e7cba",
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            {language === "ar"
              ? "جاري تحميل كامل بيانات الموظف..."
              : "Loading the complete employee record..."}
          </div>
        )}
        {fullRecordError && !isLoadingFullRecord && (
          <div
            style={{
              marginBottom: 16,
              padding: "10px 14px",
              borderRadius: 10,
              background: theme === "dark"
                ? "rgba(239,68,68,0.12)"
                : "rgba(239,68,68,0.08)",
              color: "#ef4444",
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            {language === "ar"
              ? "تعذر تحميل كامل البيانات — يتم عرض البيانات المتاحة فقط"
              : "Could not load the full record — showing locally available data only"}
          </div>
        )}

        {/* Modal Body */}
        <div style={styles.viewModalBody}>
          {/* Employee Header Section */}
          <div style={{
            ...styles.employeeHeader,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            textAlign: language === 'ar' ? 'right' : 'left'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              marginLeft: language === 'ar' || phone ? 0 : spacing.margin,
              gap: phone ? 14 : spacing.margin,
              flex: 1,
              minWidth: 0
            }}>
              {/* Profile picture */}
              {employeeProfilePics[selectedEmployee.id] ? (
                <img
                  src={employeeProfilePics[selectedEmployee.id]}
                  alt={selectedEmployee.name}
                  style={{
                    ...styles.largeAvatar,
                    objectFit: 'cover',
                    cursor: 'pointer'
                  }}
                  onClick={() => setShowFullImage(true)}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
              ) : null}

              <div
                style={{
                  ...styles.largeAvatar,
                  display: employeeProfilePics[selectedEmployee.id] ? 'none' : 'flex'
                }}
              >
                {getUserInitials(selectedEmployee?.name)}
              </div>

              {/* Employee name */}
              <div style={{
                ...styles.employeeHeaderInfo,
                textAlign: language === 'ar' ? 'right' : 'left'
              }}>
                <h3 style={styles.employeeName}>
                  {(() => {
                    const displayName = language === "ar"
                      ? (record?.arabicName || selectedEmployee?.arabicName || selectedEmployee?.name)
                      : (record?.sr || selectedEmployee?.name);

                    if (language === "ar") {
                      if (displayName && displayName.trim()) {
                        return displayName.trim();
                      }
                      return text[language].na;
                    } else {
                      if (displayName && displayName.trim()) {
                        return displayName.trim().split(/\s+/).slice(0, 3).join(" ");
                      }
                      return text[language].na;
                    }
                  })()}
                </h3>
                <p style={styles.employeeTitle}>
                  {record?.position ?? record?.employeeType ?? selectedEmployee?.position}
                </p>
                {phone && (
                  <div style={{ marginTop: 8 }}>
                    {getStatusBadge(record?.employeeState || selectedEmployee?.status)}
                  </div>
                )}
              </div>
            </div>

            {/* Status Badge on the Right */}
            {!phone && (
              <div style={{
                ...styles.employeeStatus,
                marginInlineStart: 'auto',
                marginTop: 0
              }}>
                {getStatusBadge(record?.employeeState || selectedEmployee?.status)}
              </div>
            )}
          </div>

          {/* Section tabs (shared field catalog with the edit modal) */}
          <div style={styles.tabBar} className="view-tabs-scroll">
            {visibleSections.map((section) => {
              const TabIcon = SECTION_ICONS[section.key] || FileText;
              const isActive = section.key === activeTab;
              return (
                <button
                  key={section.key}
                  type="button"
                  onClick={() => setActiveTab(section.key)}
                  onMouseEnter={(e) => {
                    if (isActive) return;
                    Object.assign(e.currentTarget.style, styles.tabButtonHover);
                  }}
                  onMouseLeave={(e) => {
                    if (isActive) return;
                    e.currentTarget.style.background = styles.tabButton.background;
                    e.currentTarget.style.color = styles.tabButton.color;
                  }}
                  style={{
                    ...styles.tabButton,
                    ...(isActive ? styles.tabButtonActive : {}),
                  }}
                >
                  <TabIcon size={phone ? 16 : 14} style={{ flexShrink: 0 }} />
                  <span style={{ minWidth: 0 }}>{section.title[language] || section.title.en}</span>
                </button>
              );
            })}
          </div>

          {/* Active tab's fields */}
          {(() => {
            const section =
              visibleSections.find((s) => s.key === activeTab) || visibleSections[0];
            const SectionIcon = SECTION_ICONS[section.key] || FileText;
            return (
              <div style={styles.infoSection}>
                {/* Phones: the selected tile already names the section. */}
                <div style={{ ...styles.sectionHeader, ...(phone && { display: "none" }) }}>
                  <SectionIcon size={screenSize === "xs" ? 16 : 18} />
                  <span>{section.title[language] || section.title.en}</span>
                </div>
                <div style={styles.infoGrid}>
                  {section.fields.map((field, i) => (
                    <div
                      key={field.name}
                      style={{
                        ...styles.infoItem,
                        ...(phone && i === section.fields.length - 1 && { borderBottom: "none" }),
                      }}
                    >
                      <div style={{ width: "100%" }}>
                        <div style={styles.infoLabel}>
                          {field.label[language] || field.label.en}
                        </div>
                        <div style={styles.infoValue}>
                          {renderFieldValue(field)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}
        </div>
      </div>

      <style>{`
        .view-tabs-scroll::-webkit-scrollbar {
          height: 4px;
        }
        .view-tabs-scroll::-webkit-scrollbar-track {
          background: transparent;
        }
        .view-tabs-scroll::-webkit-scrollbar-thumb {
          background: ${theme === "dark" ? "rgba(96,165,250,0.3)" : "rgba(46,124,186,0.25)"};
          border-radius: 999px;
        }
        .view-tabs-scroll::-webkit-scrollbar-thumb:hover {
          background: ${theme === "dark" ? "rgba(96,165,250,0.55)" : "rgba(46,124,186,0.45)"};
        }
      `}</style>
    </div>

    {/* Fullscreen image lightbox */}
    {showFullImage && employeeProfilePics[selectedEmployee.id] && (
      <div
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.6)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          padding: '2rem',
          cursor: 'zoom-out',
          animation: 'fadeIn 200ms ease-out',
        }}
        onClick={() => setShowFullImage(false)}
      >
        <img
          src={employeeProfilePics[selectedEmployee.id]}
          alt={selectedEmployee.name}
          style={{
            width: 'min(70vw, 70vh, 32rem)',
            height: 'min(70vw, 70vh, 32rem)',
            borderRadius: '50%',
            objectFit: 'cover',
            boxShadow: '0 25px 70px rgba(0, 0, 0, 0.6)',
            cursor: 'default',
          }}
          onClick={(e) => e.stopPropagation()}
        />
        <button
          onClick={() => setShowFullImage(false)}
          style={{
            position: 'fixed',
            top: '1.5rem',
            [language === 'ar' ? 'left' : 'right']: '1.5rem',
            width: '2.75rem',
            height: '2.75rem',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(255, 255, 255, 0.12)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            color: '#fff',
            cursor: 'pointer',
          }}
        >
          <X size={24} />
        </button>
      </div>
    )}
    </>
  );
};

export default ViewEmployeeModal;
